/**
 * Shared date-formatting utilities.
 *
 * The corporate display format is:
 *   - Date only:  `dd MMM yyyy`        e.g. `02 JAN 2026`
 *   - Date+time:  `dd MMM yyyy HH:mm`  e.g. `02 JAN 2026 14:30`
 *   - null / undefined / empty / invalid → `—`
 *
 * Month abbreviations are intentionally NOT derived from `Intl` / `LOCALE_ID`
 * because the French locale produces accented abbreviations with trailing dots
 * (JANV., FÉVR., DÉC.) which do not match the agreed corporate standard.
 * Instead, month strings are resolved through `ngx-translate` so the output
 * follows the language the user selected at runtime.
 *
 * Use `APP_DATE_MONTH_KEYS` to look up the 12 translation keys and pass the
 * resolved array to `formatAppDate`.  The `AppDatePipe` does this automatically.
 */

/** Translation key array for the 12 month abbreviations (index 0 = January). */
export const APP_DATE_MONTH_KEYS = [
  'general.months.jan',
  'general.months.feb',
  'general.months.mar',
  'general.months.apr',
  'general.months.may',
  'general.months.jun',
  'general.months.jul',
  'general.months.aug',
  'general.months.sep',
  'general.months.oct',
  'general.months.nov',
  'general.months.dec',
] as const;

/**
 * EN month abbreviations — for use in tests and spec fixtures only.
 *
 * This is NOT a production fallback.  All production call sites must resolve
 * months at runtime via `TranslateService` and `APP_DATE_MONTH_KEYS` (or
 * `AppDateMonthsService`) so that the active UI language is respected.
 * Passing this array from production code silently renders English months
 * regardless of the user's language.
 */
export const EN_MONTHS: readonly string[] = [
  'JAN',
  'FEB',
  'MAR',
  'APR',
  'MAY',
  'JUN',
  'JUL',
  'AUG',
  'SEP',
  'OCT',
  'NOV',
  'DEC',
];

/**
 * Formats a date value into the TotalEnergies corporate display format.
 *
 * @param value  A `Date` object, an ISO 8601 string, or `null`/`undefined`.
 * @param mode   `'date'` for date-only; `'datetime'` for date + 24-hour time.
 * @param months 12-entry array of translated month abbreviations, index 0 = January.
 *               Must be supplied by the caller — resolve it via `APP_DATE_MONTH_KEYS`
 *               and `TranslateService.instant()`, or use `AppDateMonthsService.getMonths()`.
 *               For tests, pass {@link EN_MONTHS} or a custom fixture array.
 * @returns Formatted string, or `'—'` when the value is absent or invalid.
 */
export function formatAppDate(
  value: Date | string | null | undefined,
  mode: 'date' | 'datetime',
  months: readonly string[]
): string {
  if (value == null || value === '') return '—';
  const d = value instanceof Date ? value : new Date(value as string);
  if (isNaN(d.getTime())) return '—';

  const dd = String(d.getDate()).padStart(2, '0');
  const mon = months[d.getMonth()];
  const yyyy = d.getFullYear();

  if (mode === 'datetime') {
    const HH = String(d.getHours()).padStart(2, '0');
    const mm = String(d.getMinutes()).padStart(2, '0');
    return `${dd} ${mon} ${yyyy} ${HH}:${mm}`;
  }

  return `${dd} ${mon} ${yyyy}`;
}
